/**
 * Response scoring flow — assess submitted tenders and produce an auditable
 * award recommendation.
 *
 * Intake → compliance gate → assessment → price → moderation → award →
 * assessment summaries. Every figure is computed by utils/evaluationEngine
 * from the current consensus bands, so moderation genuinely moves the result.
 */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../styles/theme';
import { Button } from '../styles/GlobalStyles';
import {
  Users, ShieldCheck, BarChart3, PoundSterling, Scale, Award, FileText, X, Lock, LockOpen,
  Globe, Plug, GitCompare, Mail, FileSignature, Rocket,
  ArrowRight, ArrowLeft, AlertTriangle, CheckCircle2, XCircle, Sparkles,
  TrendingUp, Gavel, Minus, Plus, Info, ClipboardList, ChevronUp, ChevronDown,
} from 'lucide-react';
import AgentRunConsole from '../components/evaluation/AgentRunConsole';
import FinalReportViewer from '../components/FinalReportViewer';
import {
  Shell, Eyebrow, PageTitle, PageSub, HeaderRow, ContextBar, ContextGrid, ContextItem,
  Steps, Panel, PanelHead, PanelTitle, PanelNote, PanelBody, Split, MicroLabel, Mono,
  StatTile, TileRow, Tag, Confidence, Cite, Quote, CiteFoot, BandPill, ScoreBar,
  Table, TableScroll, Actions, Callout, Num, Pulse, Processing,
} from '../components/evaluation/EvalKit';
import {
  tender, bidders, criteria, rawScores, complianceRequirements, complianceResults,
  scoringAgents, scoringLog, panel, assessorMatrix, riskFactors, priceChecks,
  scoringMethodology, anomalies, auditTrail, scoringScale, type Band,
} from '../data/evaluationMocks';
import {
  initialBands, rankAll, sensitivity, qualityBreakdown, allSubCriteria,
  isCompliant, excludedBidders, buildAssessmentSummary, divergences,
  priceWeight, totalQualityWeight, moderator, postAwardObligations,
  buildAwardLetter, buildContractDetailsNotice, type BandMap,
} from '../utils/evaluationEngine';

const STEPS = ['Submissions', 'Assessment', 'Compliance', 'Scoring', 'Price', 'Moderation', 'Award', 'Summaries', 'Contract'];

const gbp = (n: number) => `£${n.toLocaleString('en-GB')}`;

/** Steps that derive their output, and what they are doing while they do it. */
const STEP_WORK: Record<number, { title: string; lines: string[]; duration: number }> = {
  2: {
    title: 'Applying the compliance gate',
    duration: 7000,
    lines: [
      'Loading 7 mandatory requirements from the published model',
      'Testing 49 requirement-tender pairs',
      'Checking portal receipts against the 14 Aug 14:00 BST deadline',
      'Verifying certificates against issue and expiry dates',
      'Checking insurance cover and named insured entity',
      'Confirming all mandatory response sections were returned',
      'Recording determinations with reasons for the audit trail',
    ],
  },
  3: {
    title: 'Building the scoring matrix',
    duration: 9000,
    lines: [
      'Loading 45 consensus bands across 5 compliant tenders',
      'Resolving each band against the published 0–4 definitions',
      'Converting bands to points — band ÷ 4 × sub-criterion weight',
      'Aggregating 9 sub-criteria into 4 criterion subtotals',
      'Cross-checking subtotals against the published weightings',
      'Linking each band to the evidence cited at assessment',
      'Assembling the comparison matrix',
    ],
  },
  4: {
    title: 'Assessing price',
    duration: 5500,
    lines: [
      'Reading tendered prices from the pricing matrices',
      'Excluding disregarded tenders from the benchmark',
      'Identifying the lowest compliant price',
      'Normalising each tender against the benchmark',
      'Running the s.19 abnormally low tender test',
      'Checking each price against the affordability ceiling',
    ],
  },
  6: {
    title: 'Computing the award recommendation',
    duration: 8000,
    lines: [
      'Combining quality and price into weighted totals',
      'Applying the sub-criterion floor and quality gateway',
      'Testing each tender against the disqualification triggers',
      'Ranking tenders and applying the published tie-break',
      'Measuring the margin between first and second',
      'Running sensitivity against the runner-up',
      'Collecting contract risks carried into award',
    ],
  },
  7: {
    title: 'Generating assessment summaries',
    duration: 10500,
    lines: [
      'Drafting a summary for each of 7 suppliers',
      'Attaching per-criterion scores and weightings',
      'Writing the reasons awarded for each sub-criterion',
      'Embedding the evidence quoted at assessment',
      'Building the head-to-head comparison for unsuccessful tenders',
      'Setting out the reasons for each disregarded tender',
      'Adding standstill dates and the authority contact',
      'Checking each summary against section 50',
    ],
  },
  8: {
    title: 'Preparing contract documents',
    duration: 7000,
    lines: [
      'Resolving transparency obligations against the contract value',
      'Testing the £5m thresholds at sections 52 and 53(3)',
      'Carrying tendered commitments into contractual obligations',
      'Attaching conditions of award from the risk register',
      'Drafting the letter of award',
      'Assembling the contract details notice',
    ],
  },
};


/* ------------------------------------------------------------- styled ---- */

const BidderCard = styled.div<{ $excluded?: boolean }>`
  border: 1px solid ${p => p.$excluded ? '#FECACA' : theme.colors.border};
  background: ${p => p.$excluded ? '#FEF6F6' : theme.colors.surface};
  border-radius: 13px;
  padding: ${theme.spacing[4]};
  opacity: ${p => p.$excluded ? 0.9 : 1};
`;

const BidderName = styled.div`
  font-size: 13.5px; font-weight: 700; color: ${theme.colors.textPrimary};
  margin-bottom: 2px;
`;

const KV = styled.div`
  display: flex; justify-content: space-between; gap: 10px;
  font-size: 11.5px; padding: 3px 0;
  color: ${theme.colors.textMuted};
  span:last-child { color: ${theme.colors.textSecondary}; font-weight: 600; font-variant-numeric: tabular-nums; }
`;

const MatrixCell = styled.button<{ $lead?: boolean }>`
  width: 100%; display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  background: ${p => p.$lead ? theme.colors.primary[50] : 'none'};
  border: 0; padding: 7px 8px; border-radius: 8px; cursor: pointer;
  transition: background 140ms ease;
  &:hover { background: ${theme.colors.primary[100]}; }
`;

const CellPts = styled.span`
  font-size: 11.5px; color: ${theme.colors.textMuted};
  font-variant-numeric: tabular-nums; min-width: 34px; text-align: right;
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: fixed; top: 0; right: 0; bottom: 0;
  width: min(560px, 94vw);
  background: ${theme.colors.surface};
  border-left: 1px solid ${theme.colors.border};
  box-shadow: -22px 0 60px -30px rgba(0,0,60,.4);
  transform: translateX(${p => p.$open ? '0' : '100%'});
  transition: transform 280ms cubic-bezier(.4,0,.2,1);
  z-index: 1200;
  display: flex; flex-direction: column;
`;

const DrawerHead = styled.div`
  padding: ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.backgroundSecondary};
  display: flex; align-items: flex-start; gap: ${theme.spacing[3]};
`;

const DrawerBody = styled.div`padding: ${theme.spacing[5]}; overflow-y: auto; flex: 1;`;

const Scrim = styled.div<{ $open: boolean }>`
  position: fixed; inset: 0; background: rgba(0,0,40,.32);
  opacity: ${p => p.$open ? 1 : 0};
  pointer-events: ${p => p.$open ? 'auto' : 'none'};
  transition: opacity 240ms ease; z-index: 1199;
`;

const CloseBtn = styled.button`
  margin-left: auto; background: none; border: 0; cursor: pointer;
  color: ${theme.colors.textMuted}; padding: 4px; border-radius: 6px;
  &:hover { background: ${theme.colors.backgroundTertiary}; color: ${theme.colors.textPrimary}; }
`;

const Modal = styled.div`
  position: fixed; inset: 0; z-index: 1300;
  display: grid; place-items: center;
  padding: ${theme.spacing[5]};
  background: rgba(0,0,40,.34);
`;

const Sheet = styled.div`
  width: min(520px, 100%);
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 16px;
  box-shadow: 0 30px 70px -34px rgba(0,0,60,.5);
  overflow: hidden;
`;

const SheetHead = styled.div`
  padding: ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.backgroundSecondary};
`;

const SheetBody = styled.div`padding: ${theme.spacing[5]};`;

const SheetFoot = styled.div`
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-top: 1px solid ${theme.colors.border};
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const ReasonOpt = styled.label<{ $on: boolean }>`
  display: flex; align-items: flex-start; gap: 10px;
  padding: ${theme.spacing[3]};
  border: 1px solid ${p => (p.$on ? theme.colors.primary[400] : theme.colors.border)};
  background: ${p => (p.$on ? theme.colors.primary[50] : theme.colors.surface)};
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${theme.colors.textSecondary};
  transition: border-color 130ms ease, background 130ms ease;
  &:hover { border-color: ${theme.colors.primary[300]}; }

  input { margin-top: 3px; accent-color: ${theme.colors.primary[500]}; }
`;

const NoteBox = styled.textarea`
  width: 100%;
  min-height: 74px;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 13px;
  line-height: 1.55;
  color: ${theme.colors.textPrimary};
  resize: vertical;
  &:focus { outline: none; border-color: ${theme.colors.primary[400]}; }
  &::placeholder { color: ${theme.colors.textDisabled}; }
`;

const Bullet = styled.li<{ $tone: 'ok' | 'bad' }>`
  font-size: 12.5px; line-height: 1.6; margin-bottom: 6px;
  color: ${theme.colors.textSecondary};
  &::marker { color: ${p => p.$tone === 'ok' ? theme.colors.success[600] : theme.colors.warning[600]}; }
`;

const Stepper = styled.div`display: inline-flex; align-items: center; gap: 4px;`;
const StepBtn = styled.button`
  width: 24px; height: 24px; border-radius: 6px; display: grid; place-items: center;
  border: 1px solid ${theme.colors.border}; background: ${theme.colors.surface};
  cursor: pointer; color: ${theme.colors.textSecondary};
  &:hover:not(:disabled) { border-color: ${theme.colors.primary[400]}; color: ${theme.colors.primary[500]}; }
  &:disabled { opacity: .35; cursor: not-allowed; }
`;

const RankBadge = styled.div<{ $rank: number }>`
  width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center;
  font-family: ${theme.typography.fontFamily.display};
  font-weight: 700; font-size: 14px; font-variant-numeric: tabular-nums;
  background: ${p => p.$rank === 1 ? theme.colors.primary[500] : theme.colors.backgroundTertiary};
  color: ${p => p.$rank === 1 ? '#fff' : theme.colors.textMuted};
`;

const StickyRail = styled.div`
  position: sticky;
  top: ${theme.spacing[6]};
  @media (max-width: 1100px) { position: static; }
`;

/* How a finding was reached — provenance for the check itself. */
const MethodTag = styled.span<{ $m: string }>`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px; border-radius: 999px;
  font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.03em;
  background: ${p => p.$m === 'web-research' ? '#EEF2FF' : p.$m === 'external-api' ? '#ECFDF5' : theme.colors.backgroundTertiary};
  color: ${p => p.$m === 'web-research' ? '#3730A3' : p.$m === 'external-api' ? '#065F46' : theme.colors.textMuted};
  border: 1px solid ${p => p.$m === 'web-research' ? '#C7D2FE' : p.$m === 'external-api' ? '#A7F3D0' : theme.colors.border};
`;

const SourceList = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
  margin-top: 8px;
`;

const SourceChip = styled.span`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 10.5px;
  color: ${theme.colors.textMuted};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  padding: 2px 7px;
`;

const TaskRowBtn = styled.button<{ $on: boolean }>`
  width: 100%; text-align: left;
  display: flex; align-items: flex-start; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border: 0; border-bottom: 1px solid ${theme.colors.borderLight};
  background: ${p => (p.$on ? theme.colors.primary[50] : 'transparent')};
  cursor: pointer;
  transition: background 140ms ease;
  &:last-of-type { border-bottom: 0; }
  &:hover { background: ${p => (p.$on ? theme.colors.primary[100] : theme.colors.backgroundSecondary)}; }
  &:focus-visible { outline: 2px solid ${theme.colors.primary[400]}; outline-offset: -2px; }
`;

const TaskIcon = styled.span<{ $tone: 'signal' | 'ink' }>`
  flex: 0 0 auto; margin-top: 1px;
  color: ${p => (p.$tone === 'signal' ? '#B45309' : theme.colors.primary[500])};
`;

const TaskText = styled.div`
  font-size: 13px; font-weight: 600;
  color: ${theme.colors.textPrimary};
  line-height: 1.45;
`;

const TaskMeta = styled.div`
  font-size: 11.5px; color: ${theme.colors.textMuted};
  margin-top: 3px; line-height: 1.5;
`;

const RiskRow = styled.div`
  padding: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.borderLight};
  &:last-child { border-bottom: 0; }
`;

/* ---------------------------------------------------------- component ---- */

interface Adjustment {
  ts: string; sub: string; bidderId: string; from: Band; to: Band;
  by: string; reason: string; note?: string;
}

/** Grounds on which a consensus band may be moved at moderation. */
const ADJUST_REASONS = [
  'Assessor divergence resolved at moderation',
  'Clarification response accepted',
  'Evidence re-read against the published band definitions',
  'Scoring applied consistently across tenders',
  'Correction of an administrative error',
];

const ResponseScoringFlow: React.FC = () => {
  const [step, setStep] = useState(0);

  /* Each step is a state change, not a navigation, so the router's scroll
     reset does not fire. Advancing from the foot of a long step would
     otherwise drop you into the middle of the next one. */
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [step]);
  const [assessed, setAssessed] = useState(false);
  const [bands, setBands] = useState<BandMap>(initialBands);
  const [drawer, setDrawer] = useState<{ bidderId: string; sub: string } | null>(null);
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const [summaryFor, setSummaryFor] = useState('strathmere');
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const [issuedAt, setIssuedAt] = useState<string | null>(null);
  const [noticeAt, setNoticeAt] = useState<string | null>(null);
  const [contractDoc, setContractDoc] = useState<'letter' | 'notice' | null>(null);
  const [working, setWorking] = useState(false);

  /* Steps that derive their output show the derivation before the result. */
  const goStep = (n: number) => {
    setStep(n);
    const work = STEP_WORK[n];
    if (!work) { setWorking(false); return; }
    setWorking(true);
    window.setTimeout(() => setWorking(false), work.duration);
  };

  const ranked = useMemo(() => rankAll(bands), [bands]);
  const live = ranked.filter(r => !r.disqualified);
  const sens = useMemo(() => sensitivity(bands), [bands]);
  const divs = useMemo(() => divergences(bands), [bands]);
  const excluded = excludedBidders();
  const compliant = bidders.filter(b => isCompliant(b.id));
  const lateCount = bidders.filter(b => complianceResults[b.id][0].status === 'fail').length;

  const [modFilter, setModFilter] = useState<string>('all');
  const [pending, setPending] = useState<{ bidderId: string; sub: string; from: Band; to: Band } | null>(null);
  const [reason, setReason] = useState(ADJUST_REASONS[0]);
  const [note, setNote] = useState('');

  const citationCount = useMemo(
    () => Object.values(rawScores).reduce((a, entries) => a + entries.reduce((x, e) => x + e.evidence.length, 0), 0),
    []
  );
  const severityMix = useMemo(() => {
    const c = { high: 0, medium: 0, low: 0 } as Record<string, number>;
    anomalies.forEach(a => { c[a.severity] = (c[a.severity] || 0) + 1; });
    return Object.entries(c).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(' · ');
  }, []);

  const baseline = useMemo(() => rankAll(initialBands()), []);
  const rankDelta = (id: string) => {
    const now = ranked.find(r => r.bidderId === id)?.rank ?? 0;
    const was = baseline.find(r => r.bidderId === id)?.rank ?? 0;
    return was - now;
  };

  /* A band cannot move without a recorded reason, so the change is held until
     the moderator gives one. Cancelling leaves the consensus untouched. */
  const requestBand = (bidderId: string, sub: string, to: Band) => {
    const from = bands[bidderId][sub];
    if (from === to) return;
    setPending({ bidderId, sub, from, to });
    setReason(ADJUST_REASONS[0]);
    setNote('');
  };

  const commitBand = () => {
    if (!pending) return;
    const { bidderId, sub, from, to } = pending;
    setBands(b => ({ ...b, [bidderId]: { ...b[bidderId], [sub]: to } }));
    setAdjustments(a => [{
      ts: new Date().toISOString(), sub, bidderId, from, to,
      by: moderator ? `${moderator.name} (${moderator.role})` : 'Moderator',
      reason, note: note.trim() || undefined,
    }, ...a]);
    setPending(null);
  };

  const entry = drawer ? rawScores[drawer.bidderId]?.find(e => e.sub === drawer.sub) : null;
  const drawerSub = drawer ? allSubCriteria.find(s => s.id === drawer.sub) : null;
  const drawerBidder = drawer ? bidders.find(b => b.id === drawer.bidderId) : null;
  const drawerAssessors = drawer ? assessorMatrix[drawer.bidderId]?.[drawer.sub] : null;

  return (
    <Shell>
      <HeaderRow>
        <div>
          <Eyebrow>Evaluation · Response scoring</Eyebrow>
          <PageTitle>Assess and award</PageTitle>
          <PageSub>
            {bidders.length} tenders assessed against the published model, moderated to consensus,
            and ranked to an award recommendation with an assessment summary for every supplier.
          </PageSub>
        </div>
        <Actions>
          <Tag $tone="ok"><Pulse /> Evaluation open</Tag>
          <Button as={Link} to="/evaluation" $variant="outline" $size="sm"><ArrowLeft size={14} /> Workspace</Button>
        </Actions>
      </HeaderRow>

      <ContextBar>
        <ContextGrid>
          <ContextItem label="Reference" value={tender.reference} mono />
          <ContextItem label="Procurement" value={tender.title} />
          <ContextItem label="Tenders received" value={`${bidders.length}`} />
          <ContextItem label="Award basis" value="MAT" />
          <ContextItem label="Model" value={`Quality ${totalQualityWeight} / Price ${priceWeight}`} />
          <ContextItem label="Deadline passed" value="14 Aug 2026" />
          <ContextItem label="Award notice due" value="2 Sep 2026" />
        </ContextGrid>
      </ContextBar>

      <Steps labels={STEPS} current={step} onJump={goStep} />

      {/* ------------------------------------------- 0 · submissions ----- */}
      {step === 0 && (
        <>
          {!openedAt ? (
            <Panel>
              <PanelHead>
                <div>
                  <PanelTitle><Lock size={15} /> Submissions sealed</PanelTitle>
                  <PanelNote>Contents remain sealed until the tenders are formally opened.</PanelNote>
                </div>
                <Tag $tone="muted"><Lock size={11} /> Not yet opened</Tag>
              </PanelHead>
              <PanelBody>
                <div style={{ fontSize: 15, lineHeight: 1.7, color: theme.colors.textSecondary, marginBottom: theme.spacing[5] }}>
                  <strong style={{ color: theme.colors.textPrimary, fontSize: 17 }}>
                    {bidders.length} suppliers submitted a tender
                  </strong>{' '}
                  for {tender.title} (<Mono>{tender.reference}</Mono>). The submission window closed
                  on 14 August 2026 at 14:00 BST.
                </div>

                <TileRow $cols={4} style={{ marginBottom: theme.spacing[5] }}>
                  <StatTile label="Tenders received" value={bidders.length} foot={`${lateCount} after the deadline`} />
                  <StatTile label="Documents" value={bidders.reduce((a, b) => a + b.documents, 0)} foot={`${bidders.reduce((a, b) => a + b.pages, 0).toLocaleString('en-GB')} pages`} />
                  <StatTile label="SME suppliers" value={bidders.filter(b => b.smeStatus === 'SME').length} foot={`of ${bidders.length} received`} />
                  <StatTile label="Integrity" value="7 / 7" foot="SHA-256 recorded" accent />
                </TileRow>

                <TableScroll>
                  <Table>
                    <thead>
                      <tr><th>Received</th><th>Supplier</th><th>Company no.</th><th className="num">Documents</th><th>Receipt</th></tr>
                    </thead>
                    <tbody>
                      {[...bidders]
                        .sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime())
                        .map(b => {
                          const late = complianceResults[b.id][0].status === 'fail';
                          return (
                            <tr key={b.id}>
                              <td><Mono>{new Date(b.submittedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</Mono></td>
                              <td style={{ fontWeight: 600, color: theme.colors.textPrimary }}>{b.name}</td>
                              <td><Mono>{b.companyNumber}</Mono></td>
                              <td className="num">{b.documents}</td>
                              <td>
                                {late
                                  ? <Tag $tone="bad"><AlertTriangle size={10} /> After deadline</Tag>
                                  : <Tag $tone="ok"><CheckCircle2 size={10} /> In time</Tag>}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </TableScroll>

                <div style={{ marginTop: theme.spacing[5] }}>
                  <Callout $tone="info">
                    <Lock size={16} style={{ flex: '0 0 auto' }} />
                    <div>
                      Prices, technical responses and supplier documents stay sealed until opening.
                      Opening is recorded against your name and timestamped on the audit trail.
                    </div>
                  </Callout>
                </div>

                <Actions>
                  <Button onClick={() => setOpenedAt(new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }))}>
                    <LockOpen size={15} /> Open submissions
                  </Button>
                  <span style={{ fontSize: 12, color: theme.colors.textMuted }}>
                    Reveals tendered prices and submission contents to the panel.
                  </span>
                </Actions>
              </PanelBody>
            </Panel>
          ) : (
            <Panel>
              <PanelHead>
                <div>
                  <PanelTitle><LockOpen size={15} /> Tenders opened</PanelTitle>
                  <PanelNote>Opened by R. Awoyemi at {openedAt} · recorded on the audit trail.</PanelNote>
                </div>
                {lateCount > 0
                  ? <Tag $tone="warn"><AlertTriangle size={11} /> {bidders.length} received · {lateCount} after deadline</Tag>
                  : <Tag $tone="ok"><CheckCircle2 size={11} /> {bidders.length} received before deadline</Tag>}
              </PanelHead>
              <PanelBody>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: theme.spacing[4] }}>
                  {bidders.map(b => (
                    <BidderCard key={b.id}>
                      <BidderName>{b.name}</BidderName>
                      <div style={{ marginBottom: 10 }}>
                        <Tag $tone={b.smeStatus === 'SME' ? 'info' : 'muted'}>{b.smeStatus}</Tag>{' '}
                        <Tag $tone="muted">{b.hq}</Tag>
                      </div>
                      <KV><span>Tendered price</span><span>{gbp(b.price)}</span></KV>
                      <KV><span>Documents</span><span>{b.documents}</span></KV>
                      <KV><span>Pages</span><span>{b.pages}</span></KV>
                      <KV><span>Company no.</span><span>{b.companyNumber}</span></KV>
                      <KV><span>SHA-256</span><span><Mono>{b.hash}</Mono></span></KV>
                    </BidderCard>
                  ))}
                </div>
              </PanelBody>
            </Panel>
          )}

          <Split $ratio="1.2fr 1fr">
            <Panel>
              <PanelHead>
                <div>
                  <PanelTitle><Users size={15} /> Evaluation panel</PanelTitle>
                  <PanelNote>Each response is scored independently by two assessors, then moderated to a consensus.</PanelNote>
                </div>
              </PanelHead>
              <PanelBody $flush>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr><th>Assessor</th><th>Role</th><th>Scores</th><th>Conflict of interest</th></tr>
                    </thead>
                    <tbody>
                      {panel.map(p => (
                        <tr key={p.id}>
                          <td style={{ fontWeight: 600, color: theme.colors.textPrimary }}>{p.name}</td>
                          <td>{p.role}</td>
                          <td><Mono>{p.assesses.length ? p.assesses.join(' · ') : '—'}</Mono></td>
                          <td>
                            {p.coi === 'declared_none'
                              ? <Tag $tone="ok">None declared</Tag>
                              : <Tag $tone="warn" title={p.coiNote}>Declared &amp; managed</Tag>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
                {panel.find(p => p.coiNote) && (
                  <div style={{ padding: theme.spacing[4], borderTop: `1px solid ${theme.colors.borderLight}` }}>
                    <Callout $tone="warn">
                      <Info size={16} style={{ flex: '0 0 auto' }} />
                      <div><strong>Managed conflict:</strong> {panel.find(p => p.coiNote)?.coiNote}</div>
                    </Callout>
                  </div>
                )}
              </PanelBody>
            </Panel>

            <Panel>
              <PanelHead><PanelTitle><Gavel size={15} /> Published methodology</PanelTitle></PanelHead>
              <PanelBody>
                <MicroLabel>Scoring</MicroLabel>
                <p style={{ fontSize: 12.5, color: theme.colors.textSecondary, marginBottom: 14 }}>
                  {scoringMethodology.point_scale[0]}–{scoringMethodology.point_scale[1]} banded, {scoringMethodology.scoring_method},
                  normalised. Quality gateway of {scoringMethodology.minimum_qualifying_score} points.
                </p>
                <MicroLabel>Price formula</MicroLabel>
                <p style={{ fontSize: 12.5, color: theme.colors.textSecondary, marginBottom: 14 }}>
                  <Mono>{scoringMethodology.price_formula}</Mono>
                </p>
                <MicroLabel>Disqualification triggers</MicroLabel>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {scoringMethodology.disqualification_triggers.map(t => (
                    <Bullet key={t} $tone="bad">{t}</Bullet>
                  ))}
                </ul>
              </PanelBody>
            </Panel>
          </Split>

          <Actions>
            <Button onClick={() => setStep(1)} disabled={!openedAt}><Sparkles size={15} /> Run assessment</Button>
            <span style={{ fontSize: 12, color: theme.colors.textMuted }}>
              {openedAt
                ? `${bidders.reduce((a, b) => a + b.pages, 0).toLocaleString('en-GB')} pages across ${bidders.reduce((a, b) => a + b.documents, 0)} documents.`
                : 'Open the submissions before assessment can begin.'}
            </span>
          </Actions>
        </>
      )}

      {/* -------------------------------------------- 1 · assessment ----- */}
      {step === 1 && (
        <>
          <div style={{ marginBottom: theme.spacing[5] }}>
            <AgentRunConsole
              title="Tender assessment"
              subtitle={`${tender.reference} · ${bidders.length} tenders · ${bidders.reduce((a, b) => a + b.pages, 0).toLocaleString('en-GB')} pages`}
              agents={scoringAgents}
              log={scoringLog}
              onComplete={() => setAssessed(true)}
            />
          </div>

          {assessed && (
            <>
              <TileRow $cols={4} style={{ marginBottom: theme.spacing[5] }}>
                <StatTile label="Assessments produced" value={compliant.length * allSubCriteria.length} foot={`${compliant.length} tenders × ${allSubCriteria.length} sub-criteria`} />
                <StatTile label="Evidence citations" value={citationCount} foot="Every band page-anchored" />
                <StatTile label="Excluded at gate" value={excluded.length} foot={excluded.map(e => e.shortName).join(', ')} />
                <StatTile label="Flags raised" value={anomalies.length} foot={severityMix} accent />
              </TileRow>

              <Panel>
                <PanelHead>
                  <div>
                    <PanelTitle><AlertTriangle size={15} /> Integrity flags</PanelTitle>
                    <PanelNote>Cross-submission checks a reader would not catch. Each is routed and recorded.</PanelNote>
                  </div>
                </PanelHead>
                <PanelBody $flush>
                  {anomalies.map(a => (
                    <RiskRow key={a.id}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <Mono>{a.id}</Mono>
                        <strong style={{ fontSize: 13, color: theme.colors.textPrimary }}>{a.title}</strong>
                        <Tag $tone={a.severity === 'high' ? 'bad' : a.severity === 'medium' ? 'warn' : 'muted'}>{a.severity}</Tag>
                        <Tag $tone={a.status === 'resolved' ? 'ok' : 'muted'}>{a.status}</Tag>
                        {(a as any).method === 'web-research' && (
                          <MethodTag $m="web-research"><Globe size={10} /> Public records</MethodTag>
                        )}
                        {(a as any).method === 'external-api' && (
                          <MethodTag $m="external-api"><Plug size={10} /> Register API</MethodTag>
                        )}
                      </div>
                      <p style={{ fontSize: 12.5, color: theme.colors.textSecondary, margin: '0 0 6px', lineHeight: 1.6 }}>{a.detail}</p>
                      <p style={{ fontSize: 12, color: theme.colors.textMuted, margin: 0, lineHeight: 1.6 }}>
                        <strong style={{ color: theme.colors.textSecondary }}>Action:</strong> {a.action}
                      </p>
                      {(a as any).sources && (
                        <SourceList>
                          <span style={{ fontSize: 10.5, color: theme.colors.textDisabled, alignSelf: 'center' }}>Checked against</span>
                          {(a as any).sources.map((src: string) => <SourceChip key={src}>{src}</SourceChip>)}
                        </SourceList>
                      )}
                    </RiskRow>
                  ))}
                </PanelBody>
              </Panel>

              <Actions>
                <Button onClick={() => goStep(2)}>Compliance gate <ArrowRight size={15} /></Button>
              </Actions>
            </>
          )}
        </>
      )}

      {working && STEP_WORK[step] && (
        <Processing
          title={STEP_WORK[step].title}
          lines={STEP_WORK[step].lines}
          duration={STEP_WORK[step].duration}
        />
      )}

      {/* -------------------------------------------- 2 · compliance ----- */}
      {step === 2 && !working && (
        <>
          <Callout $tone="warn" style={{ marginBottom: theme.spacing[5] }}>
            <AlertTriangle size={17} style={{ flex: '0 0 auto' }} />
            <div>
              <strong>{excluded.length} tender{excluded.length === 1 ? '' : 's'} excluded before assessment.</strong> A tender that fails a mandatory
              requirement is disregarded under the Procurement Act 2023 and is neither scored nor ranked — and it does
              not set the price benchmark for the tenders that remain.
            </div>
          </Callout>

          <Panel>
            <PanelHead>
              <div>
                <PanelTitle><ShieldCheck size={15} /> Mandatory requirements</PanelTitle>
                <PanelNote>{complianceRequirements.length} gates evaluated against every tender.</PanelNote>
              </div>
            </PanelHead>
            <PanelBody $flush>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th style={{ width: 46 }}>Ref</th>
                      <th>Requirement</th>
                      <th>Basis</th>
                      {bidders.map(b => <th key={b.id} className="num">{b.shortName}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {complianceRequirements.map((req, i) => (
                      <tr key={req.id}>
                        <td><Mono>{req.id}</Mono></td>
                        <td style={{ color: theme.colors.textPrimary }}>{req.requirement}</td>
                        <td><Mono>{req.regulation_reference}</Mono></td>
                        {bidders.map(b => {
                          const r = complianceResults[b.id][i];
                          return (
                            <td key={b.id} className="num" title={r.note || r.evidence}>
                              {r.status === 'pass' && <CheckCircle2 size={16} color={theme.colors.success[600]} />}
                              {r.status === 'flag' && <AlertTriangle size={16} color={theme.colors.warning[600]} />}
                              {r.status === 'fail' && <XCircle size={16} color={theme.colors.error[600]} />}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    <tr style={{ background: theme.colors.backgroundSecondary }}>
                      <td colSpan={3} style={{ fontWeight: 700, color: theme.colors.textPrimary }}>Determination</td>
                      {bidders.map(b => (
                        <td key={b.id} className="num">
                          {isCompliant(b.id)
                            ? <Tag $tone="ok">Proceed</Tag>
                            : <Tag $tone="bad">Excluded</Tag>}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </Table>
              </TableScroll>
            </PanelBody>
          </Panel>

          {excluded.map(b => (
            <Panel key={b.id}>
              <PanelHead>
                <div>
                  <PanelTitle><XCircle size={15} color={theme.colors.error[600]} /> {b.name} — excluded</PanelTitle>
                  <PanelNote>Reasons recorded for the assessment summary and the audit trail.</PanelNote>
                </div>
                <Tag $tone="bad">
                  {complianceResults[b.id].filter(r => r.status === 'fail').length} mandatory failure
                  {complianceResults[b.id].filter(r => r.status === 'fail').length === 1 ? '' : 's'}
                </Tag>
              </PanelHead>
              <PanelBody $flush>
                {complianceResults[b.id].map((r, i) => r.status === 'fail' && (
                  <RiskRow key={i}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <XCircle size={15} color={theme.colors.error[600]} style={{ flex: '0 0 auto', marginTop: 2 }} />
                      <div>
                        <strong style={{ fontSize: 12.5, color: theme.colors.textPrimary }}>
                          {complianceRequirements[i].id} · {complianceRequirements[i].requirement}
                        </strong>
                        <p style={{ fontSize: 12.5, color: theme.colors.textSecondary, margin: '5px 0 0', lineHeight: 1.6 }}>{r.note}</p>
                        <div style={{ marginTop: 5 }}><Mono>{r.evidence}</Mono></div>
                      </div>
                    </div>
                  </RiskRow>
                ))}
              </PanelBody>
            </Panel>
          ))}

          <Actions>
            <Button onClick={() => goStep(3)}>Score the {compliant.length} compliant tenders <ArrowRight size={15} /></Button>
            <Button $variant="outline" onClick={() => setStep(1)}><ArrowLeft size={14} /> Back</Button>
          </Actions>
        </>
      )}

      {/* ------------------------------------------------ 3 · matrix ----- */}
      {step === 3 && !working && (
        <>
          <Panel>
            <PanelHead>
              <div>
                <PanelTitle><BarChart3 size={15} /> Quality scoring matrix</PanelTitle>
                <PanelNote>Consensus bands on the 0–4 scale. Select any band to read the evidence behind it.</PanelNote>
              </div>
              <Tag $tone="info">{compliant.length} × {allSubCriteria.length} = {compliant.length * allSubCriteria.length} assessments</Tag>
            </PanelHead>
            <PanelBody $flush>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th style={{ width: 54 }}>Ref</th>
                      <th>Sub-criterion</th>
                      <th className="num" style={{ width: 60 }}>Weight</th>
                      {compliant.map(b => <th key={b.id} className="num" style={{ minWidth: 130 }}>{b.shortName}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {criteria.filter(c => c.kind === 'quality').map(c => (
                      <React.Fragment key={c.id}>
                        <tr style={{ background: theme.colors.backgroundSecondary }}>
                          <td><Mono>{c.id}</Mono></td>
                          <td style={{ fontWeight: 700, color: theme.colors.textPrimary }}>{c.name}</td>
                          <td className="num" style={{ fontWeight: 700 }}>{c.weight}%</td>
                          {compliant.map(b => {
                            const pts = qualityBreakdown(b.id, bands).find(x => x.id === c.id)?.points ?? 0;
                            const best = Math.max(...compliant.map(o => qualityBreakdown(o.id, bands).find(x => x.id === c.id)?.points ?? 0));
                            return (
                              <td key={b.id} className="num" style={{ fontWeight: 700, color: pts === best ? theme.colors.primary[500] : theme.colors.textSecondary }}>
                                {pts.toFixed(2)}
                              </td>
                            );
                          })}
                        </tr>
                        {c.sub.map(s => {
                          const best = Math.max(...compliant.map(o => bands[o.id][s.id]));
                          const uniqueLead = compliant.filter(o => bands[o.id][s.id] === best).length === 1;
                          return (
                            <tr key={s.id}>
                              <td><Mono>{s.id}</Mono></td>
                              <td>{s.name}</td>
                              <td className="num">{s.weight}%</td>
                              {compliant.map(b => {
                                const band = bands[b.id][s.id];
                                return (
                                  <td key={b.id} style={{ padding: '3px 6px' }}>
                                    <MatrixCell $lead={band === best && band >= 3 && uniqueLead} onClick={() => setDrawer({ bidderId: b.id, sub: s.id })}>
                                      <CellPts>{((band / 4) * s.weight).toFixed(2)}</CellPts>
                                      <BandPill $band={band}>{band}</BandPill>
                                    </MatrixCell>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                    <tr style={{ background: theme.colors.primary[50] }}>
                      <td colSpan={2} style={{ fontWeight: 700, color: theme.colors.textPrimary }}>Quality subtotal</td>
                      <td className="num" style={{ fontWeight: 700 }}>{totalQualityWeight}%</td>
                      {compliant.map(b => {
                        const r = ranked.find(x => x.bidderId === b.id)!;
                        return <td key={b.id} className="num" style={{ fontWeight: 700, fontSize: 14, color: theme.colors.primary[500] }}>{r.quality.toFixed(2)}</td>;
                      })}
                    </tr>
                  </tbody>
                </Table>
              </TableScroll>
            </PanelBody>
          </Panel>

          <Actions>
            <Button onClick={() => goStep(4)}>Price assessment <ArrowRight size={15} /></Button>
            <Button $variant="outline" onClick={() => setStep(2)}><ArrowLeft size={14} /> Back</Button>
          </Actions>
        </>
      )}

      {/* ------------------------------------------------- 4 · price ----- */}
      {step === 4 && !working && (
        <>
          <Split $ratio="1.3fr 1fr">
            <Panel>
              <PanelHead>
                <div>
                  <PanelTitle><PoundSterling size={15} /> Price assessment</PanelTitle>
                  <PanelNote><Mono>{scoringMethodology.price_formula}</Mono></PanelNote>
                </div>
              </PanelHead>
              <PanelBody $flush>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <th>Tender</th>
                        <th className="num">Tendered price</th>
                        <th className="num">vs estimate</th>
                        <th className="num">Price score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranked.map(r => {
                        const delta = ((r.price - tender.estimatedValue) / tender.estimatedValue) * 100;
                        const lowest = Math.min(...live.map(x => x.price));
                        return (
                          <tr key={r.bidderId}>
                            <td style={{ fontWeight: 600, color: theme.colors.textPrimary }}>
                              {r.shortName}{' '}
                              {r.price === lowest && <Tag $tone="ok">benchmark</Tag>}
                            </td>
                            <td className="num">{gbp(r.price)}</td>
                            <td className="num" style={{ color: delta > 15 ? theme.colors.warning[700] : theme.colors.textMuted }}>
                              {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
                            </td>
                            <td className="num"><Num $size="14px">{r.priceScore.toFixed(2)}</Num> <span style={{ color: theme.colors.textMuted, fontSize: 11 }}>/ {priceWeight}</span></td>
                          </tr>
                        );
                      })}
                      {excluded.map(b => (
                        <tr key={b.id} style={{ opacity: .55 }}>
                          <td style={{ fontWeight: 600 }}>{b.shortName} <Tag $tone="bad">excluded</Tag></td>
                          <td className="num">{gbp(b.price)}</td>
                          <td className="num">{(((b.price - tender.estimatedValue) / tender.estimatedValue) * 100).toFixed(1)}%</td>
                          <td className="num">—</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
                <div style={{ padding: theme.spacing[4], borderTop: `1px solid ${theme.colors.borderLight}` }}>
                  <Callout $tone="info">
                    <Info size={16} style={{ flex: '0 0 auto' }} />
                    <div>
                      The excluded tender at {gbp(excluded[0]?.price ?? 0)} is the lowest received but does
                      <strong> not</strong> set the benchmark. The benchmark is the lowest <strong>compliant</strong> price,
                      {' '}{gbp(Math.min(...live.map(x => x.price)))}.
                    </div>
                  </Callout>
                </div>
              </PanelBody>
            </Panel>

            <Panel>
              <PanelHead><PanelTitle><ShieldCheck size={15} /> Price checks</PanelTitle></PanelHead>
              <PanelBody $flush>
                {priceChecks.map(pc => (
                  <RiskRow key={pc.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 12.5, color: theme.colors.textPrimary }}>{pc.label}</strong>
                      <Tag $tone={pc.outcome === 'not_triggered' ? 'ok' : 'muted'}>
                        {pc.outcome === 'not_triggered' ? 'not triggered' : 'within ceiling'}
                      </Tag>
                    </div>
                    <div style={{ marginBottom: 5 }}><Mono>{pc.basis}</Mono></div>
                    <p style={{ fontSize: 12.5, color: theme.colors.textSecondary, margin: 0, lineHeight: 1.6 }}>{pc.detail}</p>
                  </RiskRow>
                ))}
              </PanelBody>
            </Panel>
          </Split>

          <Actions>
            <Button onClick={() => setStep(5)}>Moderation <ArrowRight size={15} /></Button>
            <Button $variant="outline" onClick={() => setStep(3)}><ArrowLeft size={14} /> Back</Button>
          </Actions>
        </>
      )}

      {/* -------------------------------------------- 5 · moderation ----- */}
      {step === 5 && (
        <>
          <Callout $tone="info" style={{ marginBottom: theme.spacing[5] }}>
            <Gavel size={17} style={{ flex: '0 0 auto' }} />
            <div>
              <strong>Moderation is live.</strong> Two assessors scored each response independently.
              {' '}{divs.length} divergence{divs.length === 1 ? '' : 's'} were carried into moderation.
              Changing a consensus band here recomputes the totals, the ranking and every assessment summary immediately.
            </div>
          </Callout>

          <Split $ratio="1.5fr 1fr">
            <Panel>
              <PanelHead>
                <div>
                  <PanelTitle><Scale size={15} /> Consensus scoring</PanelTitle>
                  <PanelNote>AS1 and AS2 are the independent bands; consensus is the moderated outcome.</PanelNote>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {adjustments.length > 0 && <Tag $tone="warn">{adjustments.length} adjustment{adjustments.length === 1 ? '' : 's'}</Tag>}
                  <Button $size="sm" $variant={modFilter === 'all' ? 'primary' : 'outline'} onClick={() => setModFilter('all')}>All</Button>
                  <Button $size="sm" $variant={modFilter === 'diverged' ? 'primary' : 'outline'} onClick={() => setModFilter('diverged')}>
                    Divergences ({divs.length})
                  </Button>
                  {compliant.map(b => (
                    <Button key={b.id} $size="sm" $variant={modFilter === b.id ? 'primary' : 'outline'} onClick={() => setModFilter(b.id)}>
                      {b.shortName}
                    </Button>
                  ))}
                </div>
              </PanelHead>
              <PanelBody $flush>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <th>Tender</th><th style={{ width: 54 }}>Ref</th><th>Sub-criterion</th>
                        <th className="num">AS1</th><th className="num">AS2</th><th className="num">Consensus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compliant
                        .filter(b => modFilter === 'all' || modFilter === 'diverged' || modFilter === b.id)
                        .flatMap(b =>
                        allSubCriteria.map(s => {
                          const am = assessorMatrix[b.id]?.[s.id];
                          const band = bands[b.id][s.id];
                          const diverged = am && am.a !== am.b;
                          if (modFilter === 'diverged' && !diverged) return null;
                          return (
                            <tr key={`${b.id}-${s.id}`} style={diverged ? { background: '#FFFBEB' } : undefined}>
                              <td style={{ fontWeight: 600, color: theme.colors.textPrimary }}>{b.shortName}</td>
                              <td><Mono>{s.id}</Mono></td>
                              <td>
                                {s.name}
                                {am?.note && (
                                  <div style={{ fontSize: 11.5, color: theme.colors.warning[700], marginTop: 4, lineHeight: 1.5 }}>
                                    <AlertTriangle size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                    {am.note}
                                  </div>
                                )}
                              </td>
                              <td className="num"><BandPill $band={am?.a ?? band}>{am?.a ?? band}</BandPill></td>
                              <td className="num"><BandPill $band={am?.b ?? band}>{am?.b ?? band}</BandPill></td>
                              <td className="num">
                                <Stepper>
                                  <StepBtn disabled={band <= 0} onClick={() => requestBand(b.id, s.id, (band - 1) as Band)} aria-label={`Lower ${b.shortName} ${s.id}`}><Minus size={12} /></StepBtn>
                                  <BandPill $band={band} $lg>{band}</BandPill>
                                  <StepBtn disabled={band >= 4} onClick={() => requestBand(b.id, s.id, (band + 1) as Band)} aria-label={`Raise ${b.shortName} ${s.id}`}><Plus size={12} /></StepBtn>
                                </Stepper>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </TableScroll>
              </PanelBody>
            </Panel>

            <StickyRail>
              <Panel>
                <PanelHead><PanelTitle><TrendingUp size={15} /> Live standing</PanelTitle></PanelHead>
                <PanelBody $flush>
                  {ranked.map(r => {
                    const d = rankDelta(r.bidderId);
                    return (
                      <div key={r.bidderId} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
                        borderBottom: `1px solid ${theme.colors.borderLight}`,
                        opacity: r.disqualified ? .55 : 1,
                      }}>
                        <RankBadge $rank={r.rank}>{r.disqualified ? '—' : r.rank}</RankBadge>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.colors.textPrimary }}>{r.shortName}</div>
                          <div style={{ marginTop: 4 }}><ScoreBar quality={r.quality} price={r.priceScore} /></div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Num $size="15px">{r.total.toFixed(2)}</Num>
                          {d !== 0 && !r.disqualified && (
                            <div style={{ fontSize: 11, fontWeight: 700, color: d > 0 ? theme.colors.success[600] : theme.colors.error[600] }}>
                              {d > 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}{Math.abs(d)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </PanelBody>
              </Panel>

              {ranked.some(r => r.disqualified) && (
                <Callout $tone="bad" style={{ marginBottom: theme.spacing[5] }}>
                  <XCircle size={17} style={{ flex: '0 0 auto' }} />
                  <div>
                    <strong>Disqualification triggered.</strong>{' '}
                    {ranked.filter(r => r.disqualified).map(r => `${r.shortName}: ${r.disqualifiedFor.join('; ')}`).join(' · ')}
                  </div>
                </Callout>
              )}

              {adjustments.length > 0 && (
                <Panel>
                  <PanelHead><PanelTitle>Moderation record</PanelTitle></PanelHead>
                  <PanelBody $flush>
                    <TableScroll>
                      <Table>
                        <thead><tr><th>Time</th><th>Tender</th><th>Ref</th><th className="num">Change</th><th>Reason</th></tr></thead>
                        <tbody>
                          {adjustments.slice(0, 8).map((a, i) => (
                            <tr key={i}>
                              <td><Mono>{new Date(a.ts).toLocaleTimeString('en-GB')}</Mono></td>
                              <td>{bidders.find(b => b.id === a.bidderId)?.shortName}</td>
                              <td><Mono>{a.sub}</Mono></td>
                              <td className="num"><BandPill $band={a.from}>{a.from}</BandPill> → <BandPill $band={a.to}>{a.to}</BandPill></td>
                              <td>
                                {a.reason}
                                {a.note && <div style={{ fontSize: 11.5, color: theme.colors.textMuted, marginTop: 3, fontStyle: 'italic' }}>“{a.note}”</div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableScroll>
                  </PanelBody>
                </Panel>
              )}
            </StickyRail>
          </Split>

          <Actions>
            <Button onClick={() => goStep(6)}>Award recommendation <ArrowRight size={15} /></Button>
            <Button $variant="outline" onClick={() => setStep(4)}><ArrowLeft size={14} /> Back</Button>
            {adjustments.length > 0 && (
              <Button $variant="ghost" onClick={() => { setBands(initialBands()); setAdjustments([]); }}>Reset to assessed bands</Button>
            )}
          </Actions>
        </>
      )}

      {/* ------------------------------------------------- 6 · award ----- */}
      {step === 6 && !working && (
        <>
          {live[0] && (
            <Callout $tone="ok" style={{ marginBottom: theme.spacing[5] }}>
              <Award size={17} style={{ flex: '0 0 auto' }} />
              <div>
                <strong>{live[0].name} is the most advantageous tender</strong> at {live[0].total.toFixed(2)} of 100,
                {' '}{sens ? `${sens.margin.toFixed(2)} points` : ''} ahead of {live[1]?.name}. Tendered price {gbp(live[0].price)}.
              </div>
            </Callout>
          )}

          <TileRow $cols={4} style={{ marginBottom: theme.spacing[5] }}>
            <StatTile label="Recommended award" value={live[0]?.shortName ?? '—'} foot={gbp(live[0]?.price ?? 0)} accent />
            <StatTile label="Winning score" value={live[0]?.total.toFixed(2) ?? '—'} foot="of 100 available" />
            <StatTile label="Margin" value={sens ? sens.margin.toFixed(2) : '—'} foot={`ahead of ${sens?.challenger ?? ''}`} />
            <StatTile label="Contract risks" value={riskFactors.filter(r => r.affects.includes(live[0]?.bidderId ?? '')).length} foot="carried into award" />
          </TileRow>

          <Panel>
            <PanelHead>
              <div>
                <PanelTitle><Award size={15} /> Final ranking</PanelTitle>
                <PanelNote>Quality {totalQualityWeight} + price {priceWeight}, computed from the moderated consensus bands.</PanelNote>
              </div>
            </PanelHead>
            <PanelBody $flush>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Rank</th><th>Tender</th>
                      <th className="num">Quality</th><th className="num">Price score</th>
                      <th style={{ width: 180 }}>Composition</th>
                      <th className="num">Total</th><th className="num">Gap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranked.map(r => (
                      <tr key={r.bidderId} style={r.rank === 1 ? { background: theme.colors.primary[50] } : r.disqualified ? { opacity: .55 } : undefined}>
                        <td><RankBadge $rank={r.rank}>{r.disqualified ? '—' : r.rank}</RankBadge></td>
                        <td>
                          <div style={{ fontWeight: 700, color: theme.colors.textPrimary }}>{r.name}</div>
                          <div style={{ fontSize: 11.5, color: theme.colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>{gbp(r.price)}</div>
                        </td>
                        <td className="num">{r.quality.toFixed(2)}</td>
                        <td className="num">{r.priceScore.toFixed(2)}</td>
                        <td><ScoreBar quality={r.quality} price={r.priceScore} /></td>
                        <td className="num"><Num $size="15px">{r.total.toFixed(2)}</Num></td>
                        <td className="num" style={{ color: theme.colors.textMuted }}>{r.rank === 1 ? '—' : `−${r.gapToLeader.toFixed(2)}`}</td>
                      </tr>
                    ))}
                    {excluded.map(b => (
                      <tr key={b.id} style={{ opacity: .5 }}>
                        <td><RankBadge $rank={0}>—</RankBadge></td>
                        <td><div style={{ fontWeight: 700 }}>{b.name}</div><div style={{ fontSize: 11.5, color: theme.colors.textMuted }}>{gbp(b.price)}</div></td>
                        <td className="num" colSpan={5}><Tag $tone="bad">Excluded at compliance gate — not scored</Tag></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            </PanelBody>
          </Panel>

          <Split $ratio="1fr 1fr">
            {sens && (
              <Panel>
                <PanelHead>
                  <div>
                    <PanelTitle><TrendingUp size={15} /> How close was it?</PanelTitle>
                    <PanelNote>The question every panel is asked and few can answer quickly.</PanelNote>
                  </div>
                </PanelHead>
                <PanelBody>
                  <p style={{ fontSize: 13, color: theme.colors.textSecondary, lineHeight: 1.7, marginTop: 0 }}>
                    {sens.leader} leads {sens.challenger} by <strong>{sens.margin.toFixed(2)} points</strong>.
                    To overturn the result, {sens.challenger} would need <strong>{sens.bandsNeeded} additional band
                    {sens.bandsNeeded === 1 ? '' : 's'}</strong> across {sens.uplifts.length} sub-criteri{sens.uplifts.length === 1 ? 'on' : 'a'}:
                  </p>
                  <TableScroll>
                    <Table>
                      <thead><tr><th>Ref</th><th>Sub-criterion</th><th className="num">Band</th><th className="num">Gain</th></tr></thead>
                      <tbody>
                        {sens.uplifts.map(u => (
                          <tr key={u.sub}>
                            <td><Mono>{u.sub}</Mono></td>
                            <td>{u.name}</td>
                            <td className="num"><BandPill $band={u.from}>{u.from}</BandPill> → <BandPill $band={u.to}>{u.to}</BandPill></td>
                            <td className="num">+{u.gain.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableScroll>
                  <div style={{ marginTop: theme.spacing[4] }}>
                    <Callout $tone={sens.margin > 3 ? 'ok' : 'warn'}>
                      <CheckCircle2 size={16} style={{ flex: '0 0 auto' }} />
                      <div>
                        {sens.margin > 3
                          ? 'The outcome is robust — no single band change alters the award.'
                          : 'The outcome is finely balanced. Expect scrutiny during standstill.'}
                      </div>
                    </Callout>
                  </div>
                </PanelBody>
              </Panel>
            )}

            <Panel>
              <PanelHead>
                <div>
                  <PanelTitle><AlertTriangle size={15} /> Risks carried into award</PanelTitle>
                  <PanelNote>Scored separately from quality; mitigations become contract conditions.</PanelNote>
                </div>
              </PanelHead>
              <PanelBody $flush>
                {riskFactors.map(r => (
                  <RiskRow key={r.risk_id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                      <Mono>{r.risk_id}</Mono>
                      <strong style={{ fontSize: 12.5, color: theme.colors.textPrimary }}>{r.category}</strong>
                      <Tag $tone={r.impact === 'high' ? 'bad' : 'warn'}>{r.probability}/{r.impact}</Tag>
                      <Tag $tone="muted">{bidders.find(b => b.id === r.affects[0])?.shortName}</Tag>
                    </div>
                    <p style={{ fontSize: 12.5, color: theme.colors.textSecondary, margin: '0 0 6px', lineHeight: 1.6 }}>{r.description}</p>
                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                      {r.mitigation_required.map(m => <Bullet key={m} $tone="ok">{m}</Bullet>)}
                    </ul>
                  </RiskRow>
                ))}
              </PanelBody>
            </Panel>
          </Split>

          <Actions>
            <Button onClick={() => goStep(7)}>Generate assessment summaries <ArrowRight size={15} /></Button>
            <Button $variant="outline" onClick={() => setStep(5)}><ArrowLeft size={14} /> Back to moderation</Button>
          </Actions>
        </>
      )}

      {/* --------------------------------------------- 7 · summaries ----- */}
      {step === 7 && !working && (
        <>
          <Callout $tone="info" style={{ marginBottom: theme.spacing[5] }}>
            <FileText size={17} style={{ flex: '0 0 auto' }} />
            <div>
              <strong>Assessment summaries are a statutory requirement.</strong> Under the Procurement Act 2023 every
              supplier receives their scores with reasons — and the successful supplier's scores — before the contract
              award notice is published. An <strong>8 working day standstill</strong> then runs from publication on
              2 September to 11 September 2026.
            </div>
          </Callout>

          <TileRow $cols={4} style={{ marginBottom: theme.spacing[5] }}>
            <StatTile label="Summaries to issue" value={bidders.length} foot={`${live.length} assessed · ${excluded.length} excluded`} />
            <StatTile label="Award notice" value="2 Sep" foot="Published to Find a Tender" />
            <StatTile label="Standstill ends" value="11 Sep" foot="8 working days" accent />
            <StatTile label="Audit events" value={auditTrail.length + adjustments.length} foot="Full chain retained" />
          </TileRow>

          <Panel>
            <PanelHead>
              <div>
                <PanelTitle><FileText size={15} /> Assessment summary</PanelTitle>
                <PanelNote>Generated from the moderated bands — regenerates whenever moderation changes.</PanelNote>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {live.map(r => (
                  <Button key={r.bidderId} $size="sm" $variant={summaryFor === r.bidderId ? 'primary' : 'outline'} onClick={() => setSummaryFor(r.bidderId)}>
                    {r.shortName}
                  </Button>
                ))}
              </div>
            </PanelHead>
            <PanelBody>
              <FinalReportViewer
                key={summaryFor + adjustments.length}
                finalReport={{
                  date: new Date().toISOString(),
                  overall_score: Math.round(ranked.find(r => r.bidderId === summaryFor)?.total ?? 0),
                  max_score: 100,
                  evaluation_criteria: [],
                  submission_justification: '',
                  refined_responses: [],
                  full_report_markdown: buildAssessmentSummary(summaryFor, bands, tender),
                }}
              />
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHead>
              <div>
                <PanelTitle><ClipboardList size={15} /> Audit trail</PanelTitle>
                <PanelNote>Retained in full for the statutory record and any procurement challenge.</PanelNote>
              </div>
            </PanelHead>
            <PanelBody $flush>
              <TableScroll>
                <Table>
                  <thead><tr><th>When</th><th>Actor</th><th>Event</th><th>Detail</th></tr></thead>
                  <tbody>
                    {adjustments.map((a, i) => (
                      <tr key={`adj-${i}`} style={{ background: '#FFFBEB' }}>
                        <td><Mono>{new Date(a.ts).toLocaleString('en-GB')}</Mono></td>
                        <td>{a.by}</td>
                        <td>Consensus band adjusted</td>
                        <td>
                          {bidders.find(b => b.id === a.bidderId)?.shortName} · {a.sub} · {a.from} → {a.to} — {a.reason}
                          {a.note && <div style={{ fontSize: 11.5, color: theme.colors.textMuted, marginTop: 3, fontStyle: 'italic' }}>“{a.note}”</div>}
                        </td>
                      </tr>
                    ))}
                    {[...auditTrail].reverse().map((e, i) => (
                      <tr key={i}>
                        <td><Mono>{new Date(e.ts).toLocaleString('en-GB')}</Mono></td>
                        <td>{e.actor}</td>
                        <td style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>{e.event}</td>
                        <td>{e.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            </PanelBody>
          </Panel>

          {issuedAt && (
            <Callout $tone="ok" style={{ marginBottom: theme.spacing[5] }}>
              <CheckCircle2 size={17} style={{ flex: '0 0 auto' }} />
              <div>
                <strong>Assessment summaries issued to {bidders.length} suppliers</strong> at {issuedAt}.
                Each unsuccessful supplier has received their own scores with reasons, together with
                the scores of the successful tender.
              </div>
            </Callout>
          )}

          {noticeAt && (
            <Callout $tone="info" style={{ marginBottom: theme.spacing[5] }}>
              <Gavel size={17} style={{ flex: '0 0 auto' }} />
              <div>
                <strong>Contract award notice published to Find a Tender</strong> at {noticeAt}.
                The standstill period runs 8 working days from publication and ends
                <strong> 11 September 2026</strong>. The contract may not be entered into before then.
              </div>
            </Callout>
          )}

          <Actions>
            {!issuedAt && (
              <Button onClick={() => setIssuedAt(new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }))}>
                <FileText size={15} /> Issue summaries to {bidders.length} suppliers
              </Button>
            )}
            {issuedAt && !noticeAt && (
              <Button onClick={() => setNoticeAt(new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }))}>
                <Gavel size={15} /> Publish contract award notice
              </Button>
            )}
            {noticeAt && (
              <Button onClick={() => goStep(8)}>
                Award &amp; contract <ArrowRight size={15} />
              </Button>
            )}
            <Button as={Link} to="/evaluation" $variant="outline">
              <ArrowLeft size={14} /> Back to workspace
            </Button>
            <span style={{ fontSize: 12, color: theme.colors.textMuted }}>
              {!issuedAt
                ? 'Summaries must be issued before the award notice is published.'
                : !noticeAt
                  ? 'Publishing the notice starts the 8 working day standstill.'
                  : 'Standstill ends 11 September 2026. Contract documents follow.'}
            </span>
          </Actions>
        </>
      )}

      {/* ---------------------------------------- 8 · award & contract --- */}
      {step === 8 && !working && live[0] && (() => {
        const winner = live[0];
        const obligations = postAwardObligations(winner.price);
        return (
          <>
            <Callout $tone="ok" style={{ marginBottom: theme.spacing[5] }}>
              <CheckCircle2 size={17} style={{ flex: '0 0 auto' }} />
              <div>
                <strong>Standstill ended 11 September 2026 without challenge.</strong> The contract
                may now be entered into with {winner.name} at {gbp(winner.price)}, commencing
                1 October 2026.
              </div>
            </Callout>

            <TileRow $cols={4} style={{ marginBottom: theme.spacing[5] }}>
              <StatTile label="Supplier" value={winner.shortName} foot={gbp(winner.price)} accent />
              <StatTile label="Term" value="36 + 24" foot="months, with extension option" />
              <StatTile label="Conditions of award" value={riskFactors.filter(r => r.affects.includes(winner.bidderId)).length} foot="carried from the risk register" />
              <StatTile label="Contract start" value="1 Oct" foot="mobilisation from 15 Sep" />
            </TileRow>

            <Split $ratio="1fr 1fr">
              <Panel>
                <PanelHead>
                  <div>
                    <PanelTitle><FileSignature size={15} /> Generate documents</PanelTitle>
                    <PanelNote>Drawn from the tender, the evaluation record and the risk register.</PanelNote>
                  </div>
                </PanelHead>
                <PanelBody $flush>
                  <TaskRowBtn onClick={() => setContractDoc('letter')} $on={contractDoc === 'letter'}>
                    <TaskIcon $tone="ink"><Mail size={16} /></TaskIcon>
                    <div>
                      <TaskText>Letter of award</TaskText>
                      <TaskMeta>
                        Accepts the tender and forms the contract. Carries the supplier&rsquo;s own
                        commitments and the conditions of award across as contractual obligations.
                      </TaskMeta>
                    </div>
                    <ArrowRight size={15} color={theme.colors.textDisabled} style={{ marginLeft: 'auto', flex: '0 0 auto', alignSelf: 'center' }} />
                  </TaskRowBtn>

                  <TaskRowBtn onClick={() => setContractDoc('notice')} $on={contractDoc === 'notice'}>
                    <TaskIcon $tone="ink"><Gavel size={16} /></TaskIcon>
                    <div>
                      <TaskText>Contract details notice</TaskText>
                      <TaskMeta>
                        Statutory, within 30 days of entering the contract. Publishes final value,
                        duration and supplier to Find a Tender.
                      </TaskMeta>
                    </div>
                    <ArrowRight size={15} color={theme.colors.textDisabled} style={{ marginLeft: 'auto', flex: '0 0 auto', alignSelf: 'center' }} />
                  </TaskRowBtn>

                  <div style={{ padding: `${theme.spacing[4]}`, opacity: 0.55 }}>
                    <div style={{ display: 'flex', gap: theme.spacing[3], alignItems: 'flex-start' }}>
                      <TaskIcon $tone="ink"><Rocket size={16} /></TaskIcon>
                      <div>
                        <TaskText>Mobilisation pack</TaskText>
                        <TaskMeta>
                          Baselines the transition plan submitted with the tender. Available once
                          the supplier confirms acceptance.
                        </TaskMeta>
                      </div>
                      <Tag $tone="muted" style={{ marginLeft: 'auto', alignSelf: 'center' }}>Awaiting acceptance</Tag>
                    </div>
                  </div>
                </PanelBody>
              </Panel>

              <Panel>
                <PanelHead>
                  <div>
                    <PanelTitle><ShieldCheck size={15} /> Transparency obligations</PanelTitle>
                    <PanelNote>Which apply is decided by the contract value.</PanelNote>
                  </div>
                </PanelHead>
                <PanelBody $flush>
                  {obligations.map(o => (
                    <div key={o.id} style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start',
                      padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
                      borderBottom: `1px solid ${theme.colors.borderLight}`,
                      opacity: o.applies ? 1 : 0.62,
                    }}>
                      {o.applies
                        ? <CheckCircle2 size={15} color={theme.colors.success[600]} style={{ flex: '0 0 auto', marginTop: 2 }} />
                        : <XCircle size={15} color={theme.colors.textDisabled} style={{ flex: '0 0 auto', marginTop: 2 }} />}
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.colors.textPrimary }}>
                          {o.label} <Mono>{o.basis}</Mono>
                        </div>
                        <div style={{ fontSize: 11.5, color: theme.colors.textMuted, marginTop: 2, lineHeight: 1.5 }}>
                          {o.applies ? o.due : o.why}
                        </div>
                      </div>
                    </div>
                  ))}
                </PanelBody>
              </Panel>
            </Split>

            {contractDoc && (
              <Panel>
                <PanelHead>
                  <div>
                    <PanelTitle><FileText size={15} /> {contractDoc === 'letter' ? 'Letter of award' : 'Contract details notice'}</PanelTitle>
                    <PanelNote>Generated from the evaluation record. Export as Markdown or Word.</PanelNote>
                  </div>
                  <Button $variant="ghost" $size="sm" onClick={() => setContractDoc(null)}><X size={14} /> Close</Button>
                </PanelHead>
                <PanelBody>
                  <FinalReportViewer
                    key={contractDoc}
                    finalReport={{
                      date: new Date().toISOString(),
                      overall_score: Math.round(winner.total),
                      max_score: 100,
                      evaluation_criteria: [],
                      submission_justification: '',
                      refined_responses: [],
                      full_report_markdown: contractDoc === 'letter'
                        ? buildAwardLetter(winner.bidderId, bands, tender)
                        : buildContractDetailsNotice(winner.bidderId, bands, tender),
                    }}
                  />
                </PanelBody>
              </Panel>
            )}

            <Actions>
              <Button as={Link} to="/evaluation">
                <ArrowLeft size={14} /> Back to workspace
              </Button>
              <span style={{ fontSize: 12, color: theme.colors.textMuted }}>
                Evaluation closed. The contract record continues under contract management.
              </span>
            </Actions>
          </>
        );
      })()}

      {/* ------------------------------------------- adjustment rationale */}
      {pending && (() => {
        const sub = allSubCriteria.find(x => x.id === pending.sub);
        const bidder = bidders.find(b => b.id === pending.bidderId);
        const am = assessorMatrix[pending.bidderId]?.[pending.sub];
        const delta = ((pending.to - pending.from) / 4) * (sub?.weight ?? 0);
        return (
          <Modal onClick={() => setPending(null)}>
            <Sheet onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Record the reason for this adjustment">
              <SheetHead>
                <MicroLabel>Moderation adjustment</MicroLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                  <strong style={{ fontSize: 14, color: theme.colors.textPrimary }}>{bidder?.name}</strong>
                  <Mono>{pending.sub}</Mono>
                  <span style={{ fontSize: 13, color: theme.colors.textSecondary }}>{sub?.name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <BandPill $band={pending.from} $lg>{pending.from}</BandPill>
                  <ArrowRight size={14} color={theme.colors.textMuted} />
                  <BandPill $band={pending.to} $lg>{pending.to}</BandPill>
                  <span style={{ fontSize: 12, color: theme.colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                    {delta >= 0 ? '+' : ''}{delta.toFixed(2)} points of {sub?.weight}
                  </span>
                  {am && (
                    <span style={{ fontSize: 11.5, color: theme.colors.textDisabled }}>
                      AS1 scored {am.a} · AS2 scored {am.b}
                    </span>
                  )}
                </div>
              </SheetHead>

              <SheetBody>
                <MicroLabel>Reason</MicroLabel>
                {ADJUST_REASONS.map(r => (
                  <ReasonOpt key={r} $on={reason === r}>
                    <input type="radio" name="adjust-reason" checked={reason === r} onChange={() => setReason(r)} />
                    <span>{r}</span>
                  </ReasonOpt>
                ))}

                <div style={{ marginTop: theme.spacing[4] }}>
                  <MicroLabel>Note for the record (optional)</MicroLabel>
                  <NoteBox
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="What the panel agreed, and on what evidence."
                  />
                </div>
              </SheetBody>

              <SheetFoot>
                <Button onClick={commitBand}><CheckCircle2 size={15} /> Record adjustment</Button>
                <Button $variant="outline" onClick={() => setPending(null)}>Cancel</Button>
                <span style={{ fontSize: 11.5, color: theme.colors.textMuted, marginLeft: 'auto' }}>
                  Recorded against {moderator?.name} on the audit trail
                </span>
              </SheetFoot>
            </Sheet>
          </Modal>
        );
      })()}

      {/* ------------------------------------------------ evidence drawer */}
      <Scrim $open={!!drawer} onClick={() => setDrawer(null)} />
      <Drawer $open={!!drawer}>
        {drawer && entry && drawerSub && (
          <>
            <DrawerHead>
              <div style={{ minWidth: 0 }}>
                <MicroLabel>{drawerBidder?.name}</MicroLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Mono>{drawerSub.id}</Mono>
                  <strong style={{ fontSize: 14, color: theme.colors.textPrimary }}>{drawerSub.name}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <BandPill $band={bands[drawer.bidderId][drawer.sub]} $lg>{bands[drawer.bidderId][drawer.sub]}</BandPill>
                  <span style={{ fontSize: 12, color: theme.colors.textMuted }}>
                    {((bands[drawer.bidderId][drawer.sub] / 4) * drawerSub.weight).toFixed(2)} of {drawerSub.weight} points
                  </span>
                  <Confidence value={entry.confidence} />
                </div>
              </div>
              <CloseBtn onClick={() => setDrawer(null)}><X size={18} /></CloseBtn>
            </DrawerHead>
            <DrawerBody>
              <MicroLabel>Assessor rationale</MicroLabel>
              <p style={{ fontSize: 13, lineHeight: 1.7, color: theme.colors.textSecondary, margin: `0 0 ${theme.spacing[5]}` }}>{entry.rationale}</p>

              {drawerAssessors && (
                <div style={{ marginBottom: theme.spacing[5] }}>
                  <MicroLabel>Independent bands</MicroLabel>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12.5, color: theme.colors.textSecondary }}>AS1 <BandPill $band={drawerAssessors.a}>{drawerAssessors.a}</BandPill></span>
                    <span style={{ fontSize: 12.5, color: theme.colors.textSecondary }}>AS2 <BandPill $band={drawerAssessors.b}>{drawerAssessors.b}</BandPill></span>
                    <span style={{ fontSize: 12.5, color: theme.colors.textSecondary }}>Consensus <BandPill $band={bands[drawer.bidderId][drawer.sub]}>{bands[drawer.bidderId][drawer.sub]}</BandPill></span>
                  </div>
                  {drawerAssessors.note && (
                    <div style={{ marginTop: 10 }}>
                      <Callout $tone="warn"><Info size={15} style={{ flex: '0 0 auto' }} /><div>{drawerAssessors.note}</div></Callout>
                    </div>
                  )}
                </div>
              )}

              {entry.strengths.length > 0 && (
                <>
                  <MicroLabel>Strengths</MicroLabel>
                  <ul style={{ paddingLeft: 18, marginTop: 0, marginBottom: theme.spacing[5] }}>
                    {entry.strengths.map(s => <Bullet key={s} $tone="ok">{s}</Bullet>)}
                  </ul>
                </>
              )}

              {entry.concerns.length > 0 && (
                <>
                  <MicroLabel>Areas of concern</MicroLabel>
                  <ul style={{ paddingLeft: 18, marginTop: 0, marginBottom: theme.spacing[5] }}>
                    {entry.concerns.map(s => <Bullet key={s} $tone="bad">{s}</Bullet>)}
                  </ul>
                </>
              )}

              <MicroLabel>Evidence from the submission</MicroLabel>
              {entry.evidence.map((ev, i) => (
                <div key={i} style={{ marginBottom: theme.spacing[4] }}>
                  <Quote>“{ev.quote}”</Quote>
                  <CiteFoot>{ev.doc} · {ev.clause} · p.{ev.page}</CiteFoot>
                </div>
              ))}

              <div style={{ marginTop: theme.spacing[5], paddingTop: theme.spacing[4], borderTop: `1px solid ${theme.colors.border}` }}>
                <MicroLabel>Band definition</MicroLabel>
                <p style={{ fontSize: 12.5, color: theme.colors.textMuted, lineHeight: 1.6, margin: 0 }}>
                  <strong style={{ color: theme.colors.textPrimary }}>
                    {scoringScale.find(s => s.band === bands[drawer.bidderId][drawer.sub])?.label}
                  </strong>{' — '}
                  {scoringScale.find(s => s.band === bands[drawer.bidderId][drawer.sub])?.definition}
                </p>
              </div>
            </DrawerBody>
          </>
        )}
      </Drawer>
    </Shell>
  );
};

export default ResponseScoringFlow;
